/**
 * Who works what.
 *
 * Shifts are filled in the order they happen, across every site in the run,
 * from one pool of guards. For each one the guards who
 * could lawfully take it are scored and the best takes it; if nobody can, the
 * shift stays open rather than being given to someone it would put outside
 * the rules. A second pass then looks at contracted hours: a guard still
 * short of their weekly minimum takes a shift from a colleague who can spare
 * it without falling short themselves.
 *
 * The same inputs always give the same draft, so re-planning with one more
 * exclusion (a pairing the dry run refused) changes only what it has to.
 */

import { windowMinutes } from '../rosterCompliance/rosterModel';
import {
  GuardLedger,
  availabilityPreference,
  findBlocker,
  timeBlock,
  type TimedBlock,
} from './guardLedger';
import { isLawfulShift, planShiftBreak } from './shiftPatterns';
import { weekStartDayNumber } from './time';
import type {
  BlockerCode,
  DayDemand,
  PlanResult,
  PlannedAssignment,
  ScheduleContext,
  ScheduleGuard,
  TimeWindow,
  UnfilledBlock,
} from './types';

/** Score weights, kept together so the trade-offs are visible in one place. */
export const SCHEDULE_SCORE = {
  base: 100,
  /** Per hour a guard is still short of their contracted weekly minimum, capped. */
  perMissingContractHour: 4,
  missingContractCapHours: 24,
  /** Per hour this shift would take them past the top of their band. */
  perHourOverBand: -6,
  /** Per hour already rostered this week: spreads the rest of the work evenly. */
  perWeekHour: -1,
  /**
   * Per hour past the weekly limit inside one calendar week. The law averages
   * that limit over months, so a 72-hour week can be lawful; it is still a
   * last resort, not a plan.
   */
  perHourOverWeeklyLimit: -8,
  preferredWindow: 15,
  declaredAvailable: 8,
  /** Per previous shift at this site in the last 90 days, capped. */
  perSiteShift: 1.5,
  siteShiftCap: 10,
  /** The same shift as the day before: a run of earlies beats earlies and lates alternating. */
  sameShiftAsYesterday: 6,
} as const;

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;

/** A guard and shift the dry run has refused to put together. */
export const exclusionKey = (guardId: string, blockKey: string): string => `${guardId}|${blockKey}`;

export interface PlannerInput {
  context: ScheduleContext;
  days: DayDemand[];
  excluded?: ReadonlySet<string>;
}

/** Where the rule sets no weekly limit, the common 48 hours. */
const DEFAULT_WEEKLY_LIMIT_MINUTES = 48 * 60;

function scoreCandidate(guard: ScheduleGuard, ledger: GuardLedger, timed: TimedBlock): number {
  const weekMinutes = ledger.weekMinutes(timed.day);
  let total: number = SCHEDULE_SCORE.base;

  if (guard.contractMinWeeklyMinutes !== null) {
    const missingHours = Math.max(guard.contractMinWeeklyMinutes - weekMinutes, 0) / 60;
    total += Math.min(missingHours, SCHEDULE_SCORE.missingContractCapHours) * SCHEDULE_SCORE.perMissingContractHour;
  }
  if (guard.contractMaxWeeklyMinutes !== null) {
    const overHours = Math.max(weekMinutes + timed.dutyMinutes - guard.contractMaxWeeklyMinutes, 0) / 60;
    total += overHours * SCHEDULE_SCORE.perHourOverBand;
  }
  total += (weekMinutes / 60) * SCHEDULE_SCORE.perWeekHour;

  const weeklyLimit = timed.rule?.maxAverageWeeklyMinutes ?? DEFAULT_WEEKLY_LIMIT_MINUTES;
  const overLimitHours = (Math.max(weekMinutes + timed.dutyMinutes - weeklyLimit, 0)
    - Math.max(weekMinutes - weeklyLimit, 0)) / 60;
  total += overLimitHours * SCHEDULE_SCORE.perHourOverWeeklyLimit;

  const preference = availabilityPreference(guard, timed);
  if (preference === 'preferred') total += SCHEDULE_SCORE.preferredWindow;
  if (preference === 'available') total += SCHEDULE_SCORE.declaredAvailable;

  const siteShifts = guard.siteShiftCounts[timed.block.siteId] ?? 0;
  total += Math.min(siteShifts, SCHEDULE_SCORE.siteShiftCap) * SCHEDULE_SCORE.perSiteShift;

  const sameShiftYesterday = ledger.all().some((entry) => (
    Math.abs(entry.startMs - (timed.startMs - MS_PER_DAY)) <= MS_PER_HOUR
  ));
  if (sameShiftYesterday) total += SCHEDULE_SCORE.sameShiftAsYesterday;

  return Math.round(total * 10) / 10;
}

interface Holder {
  guard: ScheduleGuard;
  timed: TimedBlock;
}

export function planSchedule({ context, days, excluded = new Set<string>() }: PlannerInput): PlanResult {
  const { guards } = context;
  const ledgers = new Map(guards.map((guard) => [guard.id, new GuardLedger(guard.shifts)]));
  const ledgerOf = (guard: ScheduleGuard): GuardLedger => ledgers.get(guard.id)!;
  const sites = new Map(context.sites.map((siteContext) => [siteContext.site.id, siteContext]));
  const siteName = (timed: TimedBlock): string => sites.get(timed.block.siteId)?.site.name ?? '';

  // Sites share guards, so their shifts go out in the order they happen,
  // not site by site: the first site in a list gets no first pick.
  const blocks = days
    .flatMap((day) => day.blocks)
    .flatMap((block) => {
      const siteContext = sites.get(block.siteId);
      return siteContext ? [timeBlock(block, siteContext.site, siteContext.rule)] : [];
    })
    .sort((a, b) => (
      a.startMs - b.startMs
      || siteName(a).localeCompare(siteName(b))
      || a.block.siteId.localeCompare(b.block.siteId)
      || a.block.lane - b.block.lane
      || a.block.key.localeCompare(b.block.key)
    ));

  const holders = new Map<string, Holder>();

  const blockerFor = (guard: ScheduleGuard, timed: TimedBlock): BlockerCode | null => (
    excluded.has(exclusionKey(guard.id, timed.block.key))
      ? 'rule_check'
      : findBlocker(guard, ledgerOf(guard), timed, timed.rule)
  );

  const give = (guard: ScheduleGuard, timed: TimedBlock) => {
    ledgerOf(guard).add(timed);
    holders.set(timed.block.key, { guard, timed });
  };

  const tryFill = (timed: TimedBlock): boolean => {
    if (!isLawfulShift(timed.block, timed.rule)) return false;

    let best: { guard: ScheduleGuard; score: number } | null = null;
    for (const guard of guards) {
      if (blockerFor(guard, timed) !== null) continue;
      const score = scoreCandidate(guard, ledgerOf(guard), timed);
      const wins = !best
        || score > best.score
        || (score === best.score && (
          guard.fullName.localeCompare(best.guard.fullName) < 0
          || (guard.fullName === best.guard.fullName && guard.id < best.guard.id)
        ));
      if (wins) best = { guard, score };
    }

    if (!best) return false;
    give(best.guard, timed);
    return true;
  };

  blocks.forEach((timed) => { tryFill(timed); });

  // ── Contracted hours ─────────────────────────────────────────────────────
  // A guard short of their weekly minimum takes a shift from somebody who
  // stays at or above their own. Every swap shrinks the total shortfall and
  // creates none, so this settles.
  const weeks = Array.from(new Set(blocks.map((timed) => weekStartDayNumber(timed.day)))).sort((a, b) => a - b);
  const minimumOf = (guard: ScheduleGuard): number => guard.contractMinWeeklyMinutes ?? 0;
  const weekMinutesOf = (guard: ScheduleGuard, monday: number): number => ledgerOf(guard).minutesBetween(monday, monday + 6);

  const findSwap = (): { needy: ScheduleGuard; holder: Holder } | null => {
    const short = weeks
      .flatMap((monday) => guards.map((guard) => ({
        guard,
        monday,
        shortfall: minimumOf(guard) - weekMinutesOf(guard, monday),
      })))
      .filter((entry) => entry.shortfall > 0)
      .sort((a, b) => (
        b.shortfall - a.shortfall
        || a.monday - b.monday
        || a.guard.fullName.localeCompare(b.guard.fullName)
        || a.guard.id.localeCompare(b.guard.id)
      ));

    for (const { guard: needy, monday } of short) {
      const spare = Array.from(holders.values())
        .filter(({ guard: donor, timed }) => (
          donor.id !== needy.id
          && weekStartDayNumber(timed.day) === monday
          && weekMinutesOf(donor, monday) - timed.dutyMinutes >= minimumOf(donor)
        ))
        // Take from whoever has the most to spare, earliest shift first.
        .sort((a, b) => (
          (weekMinutesOf(b.guard, monday) - minimumOf(b.guard)) - (weekMinutesOf(a.guard, monday) - minimumOf(a.guard))
          || a.timed.startMs - b.timed.startMs
          || a.timed.block.key.localeCompare(b.timed.block.key)
        ));

      const holder = spare.find(({ timed }) => blockerFor(needy, timed) === null);
      if (holder) return { needy, holder };
    }
    return null;
  };

  for (let guardRail = holders.size * Math.max(guards.length, 1); guardRail > 0; guardRail -= 1) {
    const swap = findSwap();
    if (!swap) break;
    ledgerOf(swap.holder.guard).remove(swap.holder.timed.block.key);
    give(swap.needy, swap.holder.timed);
  }

  // Handing a shift on can free its old holder for one nobody could take before.
  blocks.forEach((timed) => {
    if (!holders.has(timed.block.key)) tryFill(timed);
  });

  // ── Breaks, and what is left open ────────────────────────────────────────
  // Placed last, once it is settled who works when, and stepped around the
  // breaks of colleagues at the same site that day so it is not left empty.
  const breaksBySiteDay = new Map<string, TimeWindow[]>();
  const assignments: PlannedAssignment[] = [];
  const unfilled: UnfilledBlock[] = [];

  blocks.forEach((timed) => {
    const holder = holders.get(timed.block.key);
    if (holder) {
      const siteDay = `${timed.block.siteId}|${timed.block.slotDate}`;
      const taken = breaksBySiteDay.get(siteDay) ?? [];
      const breakWindow = planShiftBreak(timed.block, timed.rule, taken);
      if (breakWindow) breaksBySiteDay.set(siteDay, [...taken, breakWindow]);
      assignments.push({
        block: timed.block,
        guardId: holder.guard.id,
        guardName: holder.guard.fullName,
        breakWindow,
      });
      return;
    }

    const reasons: UnfilledBlock['reasons'] = {};
    if (!isLawfulShift(timed.block, timed.rule)) {
      reasons.unworkable = 1;
    } else {
      guards.forEach((guard) => {
        const blocker = blockerFor(guard, timed);
        if (blocker) reasons[blocker] = (reasons[blocker] ?? 0) + 1;
      });
    }
    unfilled.push({ block: timed.block, reasons });
  });

  return { assignments, unfilled };
}

/** Minutes the draft gives out, for totals. */
export const assignedMinutes = (assignments: PlannedAssignment[]): number => (
  assignments.reduce((total, assignment) => total + windowMinutes(assignment.block), 0)
);
